import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { LayoutDashboard, Users, Building2, Wallet, UserCog, ScrollText, Shield, AlertTriangle, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader } from '@/components/Loader';

const links = [
  { to: '/admin', end: true, label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/admin/bailleurs', label: 'Bailleurs', icon: Building2 },
  { to: '/admin/locataires', label: 'Locataires', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/abonnements', label: 'Abonnements', icon: Wallet },
  { to: '/admin/catalogue', label: 'Paramètres', icon: UserCog },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
];

const ACTION_LABEL = {
  login_success: 'Connexion réussie',
  login_failure: 'Échec de connexion',
  login_blocked: 'Connexion bloquée',
  logout: 'Déconnexion',
  password_change: 'Changement de mot de passe',
  mfa_setup: 'Configuration MFA',
  mfa_verify_success: 'MFA validée',
  mfa_verify_failure: 'Échec MFA',
  refresh_token_rotated: 'Refresh token renouvelé',
  refresh_token_revoked: 'Refresh token révoqué',
  bailleur_created: 'Bailleur créé',
  bailleur_deleted: 'Bailleur supprimé',
  bailleur_deactivated: 'Bailleur désactivé',
  bailleur_reactivated: 'Bailleur réactivé',
  agent_created: 'Agent créé',
  agent_deleted: 'Agent supprimé',
  locataire_created: 'Locataire créé',
  locataire_deleted: 'Locataire supprimé',
  paiement_recorded: 'Paiement enregistré',
  subscription_updated: 'Abonnement mis à jour',
  account_anonymized: 'Compte anonymisé',
  rate_limited: 'Taux limité',
  unauthorized_access: 'Accès non autorisé',
};

const ACTION_COLOR = {
  login_success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  mfa_verify_success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  bailleur_created: 'bg-blue-100 text-blue-800 border-blue-200',
  agent_created: 'bg-blue-100 text-blue-800 border-blue-200',
  locataire_created: 'bg-blue-100 text-blue-800 border-blue-200',
  paiement_recorded: 'bg-blue-100 text-blue-800 border-blue-200',
  login_failure: 'bg-amber-100 text-amber-800 border-amber-200',
  mfa_verify_failure: 'bg-amber-100 text-amber-800 border-amber-200',
  rate_limited: 'bg-amber-100 text-amber-800 border-amber-200',
  bailleur_deleted: 'bg-red-100 text-red-800 border-red-200',
  agent_deleted: 'bg-red-100 text-red-800 border-red-200',
  locataire_deleted: 'bg-red-100 text-red-800 border-red-200',
  account_anonymized: 'bg-red-100 text-red-800 border-red-200',
  refresh_token_revoked: 'bg-red-100 text-red-800 border-red-200',
  unauthorized_access: 'bg-red-100 text-red-800 border-red-200',
  logout: 'bg-slate-100 text-slate-700 border-slate-200',
};

function formatTime(iso) {
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return iso; }
}

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [actionFilter, setActionFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 500 };
      if (actionFilter !== 'all') params.action = actionFilter;
      const { data } = await adminApi.audit(params);
      setItems(data.items || []);
      setStats(data.stats || {});
    } finally { setLoading(false); }
  }, [days, actionFilter]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const ok = items.filter((x) => x.ok).length;
    const fail = items.filter((x) => !x.ok).length;
    return { ok, fail, total: items.length };
  }, [items]);

  return (
    <AppLayout links={links} title="Journal d'audit">
      <div className="flex items-center justify-between flex-wrap gap-3 -mt-4 mb-6">
        <p className="text-slate-500 text-sm">
          Traçabilité complète des actions sensibles — connexions, créations, suppressions, paiements.
        </p>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger data-testid="audit-days-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Dernières 24h</SelectItem>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="audit-action-filter" className="w-52 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes actions</SelectItem>
              {Object.entries(ACTION_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} data-testid="audit-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="lb-card p-4 border-emerald-200 bg-emerald-50">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Actions OK</div>
          <div className="text-2xl font-bold text-emerald-800">{summary.ok}</div>
        </div>
        <div className="lb-card p-4 border-red-200 bg-red-50">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-red-700 flex items-center gap-1"><XCircle className="w-3 h-3" /> Échecs</div>
          <div className="text-2xl font-bold text-red-800">{summary.fail}</div>
        </div>
        <div className="lb-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1"><Shield className="w-3 h-3" /> Total</div>
          <div className="text-2xl font-bold text-[#0D1B2A]">{summary.total}</div>
        </div>
        <div className="lb-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Actions distinctes</div>
          <div className="text-2xl font-bold text-[#0D1B2A]">{Object.keys(stats).length}</div>
        </div>
      </div>

      <div className="lb-card overflow-hidden" data-testid="audit-table">
        {loading ? (
          <div className="p-12"><Loader /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Aucune entrée d&apos;audit pour cette période.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3">Horodatage</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Acteur</th>
                  <th className="px-4 py-3">Cible</th>
                  <th className="px-4 py-3">IP</th>
                  <th className="px-4 py-3">Détails</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600 font-mono">{formatTime(row.created_at)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`border ${ACTION_COLOR[row.action] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                        {ACTION_LABEL[row.action] || row.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {row.actor_role ? <span className="font-semibold">{row.actor_role}</span> : '—'}
                      {row.actor_id && <div className="text-[10px] text-slate-400 font-mono">{row.actor_id.slice(0, 8)}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {row.target_type ? <span className="font-semibold">{row.target_type}</span> : '—'}
                      {row.target_id && <div className="text-[10px] text-slate-400 font-mono">{String(row.target_id).slice(0, 24)}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">{row.ip || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {row.ok ? '' : <span className="text-red-600 font-semibold mr-2">ÉCHEC</span>}
                      {row.meta && Object.keys(row.meta).length > 0 && (
                        <code className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5">
                          {Object.entries(row.meta).slice(0, 3).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' ')}
                        </code>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
